import { apiClient } from '@/lib/api-client';
import type {
  GatewayCredentialsMasked, RazorpayRefund, ReconciliationSummary,
  VarianceRow, GatewaySettlement,
} from '@bbpos/types';

/** S07: a variance row also carries the bill's cash round-off; the bill took grand_total + it. */
export type BillVarianceRow = VarianceRow & { rounding_adjustment?: number };

export interface UpsertGatewayCredsInput {
  provider: string;
  key_id: string;
  key_secret: string;
  webhook_secret?: string;
  is_active?: boolean;
}

export interface InitiateRefundInput {
  bill_id: string;
  amount: number;
  reason: string;
  return_bill_id?: string;
  speed?: 'normal' | 'optimum';
}

export const paymentsService = {
  // ─── Gateway credentials (OWNER) ───────────────────────────────────────────
  getGatewayCreds(provider: string = 'razorpay') {
    return apiClient<GatewayCredentialsMasked>(
      `/payments/gateway-credentials/${encodeURIComponent(provider)}`,
    );
  },

  upsertGatewayCreds(input: UpsertGatewayCredsInput) {
    return apiClient<GatewayCredentialsMasked>('/payments/gateway-credentials', {
      method: 'PUT',
      body: input,
    });
  },

  testGatewayCreds(provider: string = 'razorpay') {
    return apiClient<{ ok: boolean; error?: string }>(
      `/payments/gateway-credentials/${encodeURIComponent(provider)}/test`,
      { method: 'POST' },
    );
  },

  deactivateGatewayCreds(provider: string = 'razorpay') {
    return apiClient<{ deactivated: boolean }>(
      `/payments/gateway-credentials/${encodeURIComponent(provider)}`,
      { method: 'DELETE' },
    );
  },

  // ─── Refunds (MANAGER+) ────────────────────────────────────────────────────
  initiateRefund(input: InitiateRefundInput) {
    return apiClient<RazorpayRefund>('/payments/refunds', {
      method: 'POST',
      body: input,
    });
  },

  getRefund(id: string) {
    return apiClient<RazorpayRefund>(`/payments/refunds/${encodeURIComponent(id)}`);
  },

  listRefundsForBill(billId: string) {
    return apiClient<RazorpayRefund[]>(`/payments/refunds?bill_id=${encodeURIComponent(billId)}`);
  },

  // ─── Reconciliation (MANAGER+) ─────────────────────────────────────────────
  reconciliationSummary(from: string, to: string) {
    return apiClient<ReconciliationSummary>(
      `/payments/reconciliation/summary?from=${from}&to=${to}`,
    );
  },

  reconciliationVariance(from: string, to: string) {
    return apiClient<BillVarianceRow[]>(
      `/payments/reconciliation/variance?from=${from}&to=${to}`,
    );
  },

  reconciliationSettlements(from: string, to: string) {
    return apiClient<GatewaySettlement[]>(
      `/payments/reconciliation/settlements?from=${from}&to=${to}`,
    );
  },
};
